import i18n from '@/utils/i18n';
import ProspectImagePlaceholder from '@assets/icons/prospect-image-placeholder.svg';
import React from 'react';
import { Badge, BadgeSize, BadgeVariant } from '@usepepper/ui/atoms/badge';
import { Button, Typography } from '@usepepper/sprout-kit-ui';
import { CachedImage } from '@usepepper/ui/atoms/cached-image';
import { createStyles } from '@/contexts/theme-context';
import { distanceByCountryCode } from '@/utils/distance';
import { GestureResponderEvent, Platform, Pressable, View } from 'react-native';
import { NearbyBusiness } from '@/experiences/sales-portal/domain/prospects/blocs/loaders/use-prospect-finder-nearby-businesses-loader';

interface Props {
  business: NearbyBusiness;
  claiming?: boolean;
  onClaimPress?: (business: NearbyBusiness) => void;
  onRowPress?: (business: NearbyBusiness) => void;
  selected?: boolean;
  supplierCountryCode: string;
}

const IMG_SIZE = 72;

// Pressable.hovered doesn't fire on native, so the Claim button is always-on there.
const isNative = Platform.OS !== 'web';

export const BusinessInfoCard: React.FC<Props> = ({
  business,
  claiming,
  onClaimPress,
  onRowPress,
  selected,
  supplierCountryCode,
}) => {
  const [styles] = useStyles();
  const isExisting = !!business.restaurantUuid;

  // Skip distance or category when either is missing.
  const subtitleParts = [
    ...(business.distance !== null && business.distance !== undefined
      ? [distanceByCountryCode(business.distance, supplierCountryCode)]
      : []),
    ...(business.categories?.length ? [business.categories[0]] : []),
  ];

  const streetAddress = business.displayAddress?.split(',')[0]?.trim();

  return (
    <Pressable
      accessibilityLabel={business.businessName}
      accessibilityState={{ selected: !!selected }}
      onPress={() => onRowPress?.(business)}
      style={({ hovered }) => [
        styles.row,
        hovered && styles.rowHovered,
        selected && styles.rowSelected,
      ]}
    >
      {({ hovered }) => (
        <>
          {business.imageUrl ? (
            <CachedImage
              accessibilityLabel={i18n.t('business_image', {
                businessName: business.businessName,
              })}
              contentFit="cover"
              source={{ uri: business.imageUrl }}
              style={styles.image}
            />
          ) : (
            <ProspectImagePlaceholder
              accessibilityLabel={i18n.t('business_image_placeholder', {
                businessName: business.businessName,
              })}
              height={IMG_SIZE}
              style={styles.image}
              width={IMG_SIZE}
            />
          )}

          <View style={styles.content}>
            <Typography fontWeight="500" numberOfLines={1} size="$sm">
              {business.businessName}
            </Typography>
            {streetAddress ? (
              <Typography color="$textSecondary" numberOfLines={1} size="$xs">
                {streetAddress}
              </Typography>
            ) : null}
            {subtitleParts.length > 0 ? (
              <Typography
                accessibilityLabel={subtitleParts.join(', ')}
                color="$textSecondary"
                numberOfLines={1}
                size="$xs"
              >
                {subtitleParts.join(' • ')}
              </Typography>
            ) : null}
          </View>

          {isExisting ? (
            <View style={styles.actionSlot}>
              <Badge
                label={i18n.t(
                  'sales_portal.prospect_finder.row.existing_badge',
                )}
                size={BadgeSize.SMALL}
                style={styles.existingBadgeOverride}
                textStyle={styles.existingBadgeText}
                variant={BadgeVariant.DEFAULT}
              />
            </View>
          ) : (
            <View
              pointerEvents={hovered || isNative ? 'auto' : 'none'}
              style={[
                styles.actionSlot,
                hovered || isNative ? styles.claimVisible : styles.claimHidden,
              ]}
            >
              <Button
                loading={!!claiming}
                onPress={(event: GestureResponderEvent) => {
                  // Claim acts directly; don't also select the row.
                  event.stopPropagation();
                  onClaimPress?.(business);
                }}
                size="$sm"
                variant="secondary"
              >
                {i18n.t('sales_portal.prospect_finder.row.claim_button')}
              </Button>
            </View>
          )}
        </>
      )}
    </Pressable>
  );
};

const useStyles = createStyles((theme) => ({
  actionSlot: {
    flexShrink: 0,
  },
  claimHidden: {
    opacity: 0,
  },
  claimVisible: {
    opacity: 1,
  },
  content: {
    flex: 1,
    justifyContent: 'flex-start',
  },
  existingBadgeOverride: {
    borderColor: theme.sk.color.border.secondary,
    borderWidth: 1,
  },
  existingBadgeText: {
    color: theme.sk.color.text.secondary,
  },
  image: {
    borderColor: theme.sk.color.border.secondary,
    borderRadius: theme.sk.radii.radiusMedium,
    borderWidth: 1,
    height: IMG_SIZE,
    width: IMG_SIZE,
  },
  row: {
    alignItems: 'flex-start',
    flexDirection: 'row',
    gap: theme.sk.spacing.spaceSMedium,
    paddingHorizontal: theme.sk.spacing.spaceMedium,
    paddingVertical: theme.sk.spacing.spaceSMedium,
  },
  rowHovered: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
  rowSelected: {
    backgroundColor: theme.sk.color.bg.secondary.default,
  },
}));
